/**
 * Renders the CRM's verbatim section notes (A7Y-4982) — the markdown the
 * importer kept from `pipeline.md` — as React elements, never as HTML.
 *
 * It covers what those sections actually use: paragraphs, `-`/`*` bullets,
 * `#` headings, `**bold**`, `*italic*`/`_italic_`, `~~strike~~` (A7Y-5492: a
 * departed contact's note), `` `code` `` and `[label](target)` links; a `§`
 * reads "section", or "account page" as a link to one (A7Y-5873). Link targets go through `resolveLink`, so a relative
 * path opens the file in a7y-ai-dev, an `#acct-…` anchor opens that account,
 * and anything else that is not http(s) renders as plain text.
 *
 * Since A7Y-5342 every record text a HELM page shows goes through here —
 * history lines, next actions, notes, slot notes, roster rows — so no page
 * prints raw `**bold**` or `[note](bd-sync/…)` again. `RowText` is HELM's one
 * way to show a roster row: what it is about, its next action
 * (`helmRowSummary`, A7Y-5492), and the full text behind "Show more".
 * `AccountNameLink` is the one way to show an account's name: the name links
 * to the account page and a link written into it renders as one.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { helmLinkLabel, helmRowSummary, helmSectionSigns } from '@a7y/platform-health/helm';
import { accountPagePath, resolveLink, splitAccountName } from './links';

const INLINE_RE =
  /(\*\*[^*]+\*\*|~~[^~]+~~|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*\s][^*]*\*|_[^_\s][^_]*_)/g;

export function CrmLink({ target, children }: { target: string; children: ReactNode }) {
  const link = resolveLink(target);
  if (link === null) return <span>{children}</span>;
  const className = 'text-blue-600 hover:underline dark:text-blue-400';
  if (link.internal)
    return (
      <Link to={link.href} className={className}>
        {children}
      </Link>
    );
  return (
    <a href={link.href} target="_blank" rel="noreferrer" className={className}>
      {children}
    </a>
  );
}

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE_RE)) {
    const token = m[0];
    const at = m.index;
    if (at > last) out.push(helmSectionSigns(text.slice(last, at)));
    const key = `${keyPrefix}-${i++}`;
    // A bold run may hold a link (`**10/8 ([note](bd-sync/…)):**`, A7Y-5342): its content is rendered too.
    if (token.startsWith('**'))
      out.push(<strong key={key}>{inline(token.slice(2, -2), key)}</strong>);
    else if (token.startsWith('~~')) out.push(<s key={key}>{inline(token.slice(2, -2), key)}</s>);
    else if (token.startsWith('`'))
      out.push(
        <code key={key} className="rounded bg-zinc-100 px-1 text-xs dark:bg-zinc-800">
          {token.slice(1, -1)}
        </code>
      );
    else if (token.startsWith('[')) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token)!;
      // A7Y-5873: a `§` label reads "account page" or "section", never the sign.
      out.push(
        <CrmLink key={key} target={link[2]}>
          {helmLinkLabel(link[1], link[2])}
        </CrmLink>
      );
    } else out.push(<em key={key}>{inline(token.slice(1, -1), key)}</em>);
    last = at + token.length;
  }
  if (last < text.length) out.push(helmSectionSigns(text.slice(last)));
  return out;
}

type Block = { kind: 'p' | 'h'; text: string } | { kind: 'ul'; items: string[] };

function blocks(markdown: string): Block[] {
  const out: Block[] = [];
  let para: string[] = [];
  let list: string[] | null = null;
  const flush = () => {
    if (para.length) out.push({ kind: 'p', text: para.join(' ') });
    if (list) out.push({ kind: 'ul', items: list });
    para = [];
    list = null;
  };
  for (const raw of markdown.split(/\r?\n/)) {
    const line = raw.trim();
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    if (line === '') flush();
    else if (bullet) {
      if (para.length) flush();
      list = [...(list ?? []), bullet[1]];
    } else if (heading) {
      flush();
      out.push({ kind: 'h', text: heading[1] });
    } else if (list && /^\s/.test(raw)) {
      // An indented continuation of the last bullet.
      list[list.length - 1] = `${list[list.length - 1]} ${line}`;
    } else {
      if (list) flush();
      para.push(line);
    }
  }
  flush();
  return out;
}

export function CrmMarkdown({ markdown }: { markdown: string }) {
  return (
    <div className="space-y-2 text-sm text-zinc-800 dark:text-zinc-200">
      {blocks(markdown).map((block, i) => {
        if (block.kind === 'ul')
          return (
            <ul key={i} className="list-disc space-y-1 pl-5">
              {block.items.map((item, j) => (
                <li key={j}>{inline(item, `${i}-${j}`)}</li>
              ))}
            </ul>
          );
        if (block.kind === 'h')
          return (
            <p key={i} className="font-semibold text-zinc-900 dark:text-white">
              {inline(block.text, String(i))}
            </p>
          );
        return <p key={i}>{inline(block.text, String(i))}</p>;
      })}
    </div>
  );
}

/** One line of record text with its marks and links rendered, never as raw markdown. */
export function InlineMarkdown({ text }: { text: string }) {
  return <>{inline(text, 'i')}</>;
}

/** "Show more" for a row whose summary left text out; the full text opens below it. */
export function RowMore({
  markdown,
  testId = 'helm-row-more',
}: {
  markdown: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!helmRowSummary(markdown).more) return null;
  return (
    <span className="block">
      <button
        type="button"
        data-testid={testId}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="min-h-6 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
      >
        {open ? 'Show less' : 'Show more'}
      </button>
      {open ? (
        <span className="mt-1 block" data-testid={`${testId}-text`}>
          <CrmMarkdown markdown={markdown} />
        </span>
      ) : null}
    </span>
  );
}

/** A roster row: what it is about in bold, its next action, and the rest behind "Show more" (A7Y-5342, A7Y-5492). */
export function RowText({
  markdown,
  decision = false,
  testId = 'helm-row-text',
}: {
  markdown: string;
  /** A decision row's subject is its decision; with nothing else it reads alone. */
  decision?: boolean;
  testId?: string;
}) {
  const { ask, next } = helmRowSummary(markdown, { decision });
  return (
    <span data-testid={testId}>
      <span className="font-medium">{ask}</span>
      {next ? (
        <span className="text-zinc-600 dark:text-zinc-400" data-testid={`${testId}-next`}>
          {' '}
          — {next}
        </span>
      ) : null}
      <RowMore markdown={markdown} testId={`${testId}-more`} />
    </span>
  );
}

/** An account's name linked to its page, and any link written into the name rendered beside it — never raw markdown. */
export function AccountNameLink({
  accountId,
  name,
  className = 'font-medium text-blue-600 hover:underline dark:text-blue-400',
  testId,
}: {
  accountId: string;
  name: string;
  className?: string;
  testId?: string;
}) {
  const parts = splitAccountName(name);
  return (
    <>
      <Link to={accountPagePath(accountId)} className={className} data-testid={testId}>
        {parts.name}
      </Link>
      {parts.rest ? (
        <span className="text-zinc-600 dark:text-zinc-400">
          {' '}
          <InlineMarkdown text={parts.rest} />
        </span>
      ) : null}
    </>
  );
}
